import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuildingAPI, FloorAPI, DestinationAPI, QrAPI, resolveAsset } from "../../services/api";
import { ReactComponent as DownloadIcon } from "../../assets/icons/listallbuilding/download.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/dashboard/eye.svg";
import { ReactComponent as FindIcon } from "../../assets/icons/dashboard/magnifyingglass.svg";
import { ReactComponent as MeetingRoomIcon } from "../../assets/icons/dashboard/meetingroom.svg";
import { ReactComponent as QrCodeIcon } from "../../assets/icons/listallbuilding/qrcode.svg";
import { ReactComponent as QrCodeScannerIcon } from "../../assets/icons/dashboard/qrcodescanner.svg";
import { ReactComponent as StairsIcon } from "../../assets/icons/listallbuilding/stairs.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/listallbuilding/edit.svg";

export default function ListAllBuilding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Param
  const buildingId = searchParams.get("buildingId");

  // State
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  // Stats
  const [floorsCount, setFloorsCount] = useState(0);
  const [registeredLocationsCount, setRegisteredLocationsCount] = useState(0);
  const [activeQrCount, setActiveQrCount] = useState(0);
  const [totalUsageCount, setTotalUsageCount] = useState(0);

  // Table & QRs
  const [allQrCodes, setAllQrCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const itemsPerPage = 6;

  // 1. Fetch buildings list
  useEffect(() => {
    BuildingAPI.list()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setBuildings(list);
      })
      .catch(() => setBuildings([]));
  }, []);

  // 2. Set default building if not specified
  useEffect(() => {
    if (!buildingId && buildings.length > 0) {
      setSearchParams({ buildingId: buildings[0].id }, { replace: true });
    }
  }, [buildingId, buildings, setSearchParams]);

  // 3. Load selected building data
  useEffect(() => {
    if (!buildingId) return;

    setLoading(true);
    setErrorMsg("");

    // Fetch building details
    BuildingAPI.get(buildingId)
      .then((b) => {
        setSelectedBuilding(b);
      })
      .catch(() => {
        setSelectedBuilding(null);
      });

    // Fetch floors and destinations
    FloorAPI.listByBuilding(buildingId)
      .then(async (floors) => {
        const list = Array.isArray(floors) ? floors : floors?.items || [];
        setFloorsCount(list.length);

        try {
          const destPromises = list.map((floor) =>
            DestinationAPI.listByFloor(floor.id).catch(() => [])
          );
          const destLists = await Promise.all(destPromises);
          const totalDests = destLists.reduce((sum, dList) => {
            const items = Array.isArray(dList) ? dList : dList?.items || [];
            return sum + items.length;
          }, 0);
          setRegisteredLocationsCount(totalDests);
        } catch {
          setRegisteredLocationsCount(0);
        }
      })
      .catch(() => {
        setFloorsCount(0);
        setRegisteredLocationsCount(0);
      });

    // Fetch QR codes
    QrAPI.list({ buildingId, pageSize: 1000 })
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setAllQrCodes(list);

        // Compute QR stats
        const activeCount = list.filter((q) => q.status === "active").length;
        const usageCount = list.reduce(
          (sum, q) => sum + (Number(q.scanCount || q.scans) || 0),
          0
        );

        setActiveQrCount(activeCount);
        setTotalUsageCount(usageCount);
      })
      .catch((e) => {
        setAllQrCodes([]);
        setActiveQrCount(0);
        setTotalUsageCount(0);
        setErrorMsg(e.message || t("admin.qrList.loadFailed"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [buildingId, t]);

  // Derived / Filtered data for Table
  const filteredQrCodes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allQrCodes;
    return allQrCodes.filter(
      (item) =>
        (item.code || "").toLowerCase().includes(q) ||
        (item.name || "").toLowerCase().includes(q)
    );
  }, [allQrCodes, searchQuery]);

  const totalPages = Math.max(Math.ceil(filteredQrCodes.length / itemsPerPage), 1);
  const paginatedTableData = useMemo(() => {
    return filteredQrCodes.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [filteredQrCodes, page]);

  // Helper functions
  const handleToggle = async (qr) => {
    const nextStatus = qr.status === "active" ? "inactive" : "active";
    // optimistic update
    setAllQrCodes((prev) =>
      prev.map((x) => (x.id === qr.id ? { ...x, status: nextStatus } : x))
    );
    try {
      await QrAPI.setStatus(qr.id, nextStatus);
      // Recalculate active QR count
      setActiveQrCount((prev) => (nextStatus === "active" ? prev + 1 : prev - 1));
    } catch (e) {
      // rollback
      setAllQrCodes((prev) =>
        prev.map((x) => (x.id === qr.id ? { ...x, status: qr.status } : x))
      );
      alert(e.message || t("admin.qrList.statusChangeFailed"));
    }
  };

  const onExport = () => {
    const header = [
      "code",
      "name",
      "x",
      "y",
      "scanCount",
      "status",
      "createdAt",
    ];
    const rows = allQrCodes.map((q) =>
      header.map((k) => JSON.stringify(q[k] ?? "")).join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-codes-${selectedBuilding?.name || "building"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    {
      title: t("admin.listAllBuilding.floorsCount"),
      value: String(floorsCount),
      tone: "blue",
      point: "ชั้น",
      icon: StairsIcon,
    },
    {
      title: t("admin.listAllBuilding.registeredLocations"),
      value: String(registeredLocationsCount),
      tone: "purple",
      point: "ห้อง",
      icon: MeetingRoomIcon,
    },
    {
      title: t("admin.listAllBuilding.activeQrCodes"),
      value: String(activeQrCount),
      tone: "green",
      point: "จุด",
      icon: QrCodeScannerIcon,
    },
    {
      title: t("admin.listAllBuilding.totalUsage"),
      value: totalUsageCount >= 1000 ? `${(totalUsageCount / 1000).toFixed(1)}k` : String(totalUsageCount),
      tone: "orange",
      point: "ครั้ง",
      icon: EyeIcon,
    },
  ];

  function statCardBg(tone) {
    switch (tone) {
      default:
        return "bg-white border-slate-200";
    }
  }

  const TextPill = (tone) => {
    switch (tone) {
      case "orange":
        return "text-orange-900";
      case "green":
        return "text-green-900";
      case "blue":
        return "text-blue-900";
      case "purple":
        return "text-violet-900";
      default:
        return "text-slate-900";
    }
  };

  const TextTone = (tone) => {
    switch (tone) {
      case "orange":
        return "text-orange-600";
      case "green":
        return "text-green-600";
      case "blue":
        return "text-blue-600";
      case "purple":
        return "text-violet-600";
      default:
        return "text-slate-600";
    }
  };

  const IconPill = (tone) => {
    switch (tone) {
      case "orange":
        return "bg-orange-50 text-orange-600";
      case "green":
        return "bg-green-50 text-green-600";
      case "blue":
        return "bg-blue-50 text-blue-600";
      case "purple":
        return "bg-violet-50 text-violet-600";
      default:
        return "bg-slate-50 text-slate-600";
    }
  };

  return (
    <div className="font-sans">
      {/* Title & Dropdown Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 m-1">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {t("admin.layout.buildingInfoHeader")}
          </h1>
        </div>
        {buildings.length > 0 && (
          <select
            value={buildingId || ""}
            onChange={(e) => {
              setSearchParams({ buildingId: e.target.value });
              setPage(1);
            }}
            className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm min-w-[240px] shadow-sm font-semibold text-slate-700 outline-none focus:border-blue-500 transition-colors"
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Cover Image Banner */}
      <div className="mb-3">
        <div className="m-1">
          <div
            className="w-full h-56 rounded-xl flex text-center items-end justify-end transition-all duration-300"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.3)), url(${
                resolveAsset(
                  (selectedBuilding?.name?.toLowerCase().includes("s1") || String(selectedBuilding?.code || selectedBuilding?.id || "").toLowerCase().includes("s1"))
                    ? "https://asset.oou.cmu.ac.th/wp-content/uploads/2025/06/19429952_1374719025916362_5971276997021923822_n.jpg"
                    : (selectedBuilding?.imageUrl || selectedBuilding?.img)
                ) || "https://cdn.chiangmainews.co.th/wp-content/uploads/2021/10/20105616/2018-10_f66c5246b37fdec.jpg"
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {buildingId && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/admin/buildings/add?buildingId=${encodeURIComponent(buildingId)}`)
                }
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center text-sm font-semibold rounded-lg pt-2 pb-2 pl-4 pr-4 mb-3 mr-3 cursor-pointer transition-colors shadow-lg"
              >
                {t("admin.listAllBuilding.editBuildingInfo")}{" "}
                <EditIcon className="w-4 h-4 text-white ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 m-1">
        {/* STATS CARDS */}
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {statCards.map((c) => {
            const Icon = c.icon;

            return (
              <div
                key={c.title}
                className={[
                  "rounded-xl border p-5 shadow-sm transition-all duration-200 hover:shadow-md",
                  statCardBg(c.tone),
                ].join(" ")}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`shrink-0 w-16 h-16 rounded-full grid place-items-center ${IconPill(
                      c.tone
                    )}`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold ${TextTone(c.tone)}`}>
                      {c.title}
                    </div>

                    <div className="flex items-end gap-6 mt-2">
                      <div
                        className={`text-3xl ml-4 font-bold ${TextPill(c.tone)}`}
                      >
                        {loading ? "..." : c.value}
                      </div>

                      <div className="text-sm font-semibold text-slate-600 mb-1">
                        {c.point}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Table Card */}
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div className="px-4 py-4 flex items-center justify-between">
            {/* Search */}
            <div className="hidden md:flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2.5 min-w-[320px]">
              <FindIcon className="w-4 h-4 text-slate-500" />
              <input
                className="w-full outline-none text-sm font-base text-slate-700 placeholder:text-slate-400"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder={t("common.search")}
              />
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={onExport}
                className="flex px-4 py-2 bg-white text-slate-600 hover:bg-slate-50 rounded-lg font-semibold text-sm border border-gray-200 transition-colors"
              >
                <DownloadIcon className="w-4 h-4 text-slate-700 mr-2" />{" "}
                {t("admin.listAllBuilding.exportAll")}
              </button>
              {buildingId && (
                <button
                  onClick={() =>
                    navigate(`/admin2/setqr?buildingId=${encodeURIComponent(buildingId)}`)
                  }
                  className="flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-colors shadow-sm"
                >
                  <QrCodeIcon className="w-5 h-5 font-semibold mr-2 text-white" />{" "}
                  {t("admin.listAllBuilding.createNewQr")}
                </button>
              )}
            </div>
          </div>

          <div className="m-4 border border-slate-200 rounded-xl overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="min-w-[30px] px-3 py-3 text-center"></th>
                    <th className="min-w-[35px] px-3 py-3 text-center whitespace-nowrap">
                      {t("admin.listAllBuilding.qrCodeCol")}
                    </th>
                    <th className="min-w-[150px] px-3 py-3 text-left whitespace-nowrap">
                      {t("admin.listAllBuilding.qrNameCol")}
                    </th>
                    <th className="min-w-[120px] px-3 py-3 text-left whitespace-nowrap">
                      {t("admin.listAllBuilding.startLocationCol")}
                    </th>
                    <th className="hidden md:table-cell min-w-[150px] px-3 py-3 text-left whitespace-nowrap">
                      {t("admin.listAllBuilding.createdDateCol")}
                    </th>
                    <th className="hidden md:table-cell min-w-[130px] px-3 py-3 text-left whitespace-nowrap">
                      {t("admin.listAllBuilding.userCountCol")}
                    </th>
                    <th className="min-w-[120px] px-3 py-3 text-center whitespace-nowrap">
                      {t("admin.listAllBuilding.statusCol")}
                    </th>
                    <th className="min-w-[110px] px-3 py-3 text-center whitespace-nowrap">
                      {t("admin.listAllBuilding.onOffCol")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={8} className="py-10 text-center text-slate-400 font-medium">
                        {t("common.loading")}
                      </td>
                    </tr>
                  )}

                  {!loading && paginatedTableData.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-10 text-center text-slate-400 font-medium">
                        {errorMsg || t("admin.qrList.noQrData")}
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedTableData.map((item) => {
                      const isActive = item.status === "active";
                      return (
                        <tr
                          key={item.id}
                          className="border-t hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/admin/qr/${item.id}`)}
                        >
                          <td className="px-3 py-3">
                            <div className="w-10 h-10 rounded-md border border-slate-200 bg-white flex items-center justify-center p-0.5 shadow-sm mx-auto">
                              <img
                                src={QrAPI.imageUrl(item.id, 64, item.code)}
                                alt={item.code}
                                className="w-8 h-8 object-contain"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            </div>
                          </td>

                          <td className="min-w-[35px] px-3 py-3 text-center font-semibold text-green-700 whitespace-nowrap">
                            {item.code}
                          </td>

                          <td className="px-3 py-3 text-slate-800 font-medium">
                            <div className="truncate max-w-[180px]">
                              {item.name}
                            </div>
                          </td>

                          <td className="px-3 py-3 text-slate-600 font-medium">
                            <div className="truncate max-w-[180px]">
                              X : {Number(item.x).toFixed(1)} Y : {Number(item.y).toFixed(1)}
                            </div>
                          </td>

                          <td className="hidden md:table-cell px-3 py-3 text-slate-600 whitespace-nowrap font-medium">
                            {formatDate(item.createdAt, i18n.language)}
                          </td>

                          <td className="hidden md:table-cell px-3 py-3 text-slate-800 font-semibold whitespace-nowrap">
                            {(item.scanCount ?? item.scans ?? 0).toLocaleString()}
                          </td>

                          <td className="px-3 py-1.5 text-center">
                            <span
                              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-1 text-xs font-semibold whitespace-nowrap ${isActive
                                  ? "bg-green-50 text-green-700 border-green-700"
                                  : "bg-slate-100 text-slate-500 border-slate-200"
                                }`}
                            >
                              <span
                                className={`w-2 h-2 rounded-full ${isActive ? "bg-green-700" : "bg-slate-400"
                                  }`}
                              />
                              {isActive
                                ? t("admin.listAllBuilding.activeStatus")
                                : t("admin.qrList.inactive")}
                            </span>
                          </td>

                          <td className="px-3 py-2">
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggle(item);
                                }}
                                className={`relative h-7 w-12 rounded-full transition-colors ${isActive ? "bg-green-700" : "bg-slate-300"
                                  }`}
                              >
                                <span
                                  className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${isActive ? "left-6" : "left-1"
                                    }`}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-5 mb-6">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="border border-slate-200 bg-white rounded-xl px-3 py-2 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
              >
                ‹
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                const isCurrent = p === page;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={
                      "rounded-xl px-2 py-1.5 text-sm font-semibold border transition-all " +
                      (isCurrent
                        ? "border-blue-600 bg-blue-600 text-white font-semibold shadow-sm"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                    }
                  >
                    {String(p).padStart(2, "0")}
                  </button>
                );
              })}

              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="border border-slate-200 bg-white rounded-xl px-3 py-2 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
              >
                ›
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <div className="mt-3 m-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}

function formatDate(s, lng) {
  if (!s) return "-";
  try {
    const locale = lng === "en" ? "en-US" : "th-TH";
    return new Date(s).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}
